import { NavigationContext } from "@react-navigation/native";
import { ReactNode, useContext, useEffect, useRef, useState } from "react";

import { LoaderActivityProvider } from "@whoosh/design-system/src/components/display";

import {
  useAppliedLanguage,
  useReportLocaleBoundaryLoaderActivity,
} from "./LanguagePreferenceProvider";

type LocaleTransitionBoundaryProps = {
  children: ReactNode;
};

/**
 * Remounts translated content after a locale change so components that
 * imperatively translate or format values do not retain output from the
 * previous locale. Locale-source providers must remain above this boundary.
 */
export const LocaleTransitionBoundary = ({
  children,
}: LocaleTransitionBoundaryProps) => {
  const appliedLanguage = useAppliedLanguage();
  const reportLoaderActivity = useReportLocaleBoundaryLoaderActivity();
  const navigation = useContext(NavigationContext);
  const boundaryId = useRef(Symbol("locale-transition-boundary")).current;
  const [activeLoaderCount, setActiveLoaderCount] = useState(0);
  const [active, setActive] = useState(() => navigation?.isFocused() ?? true);

  useEffect(() => {
    if (!navigation) {
      setActive(true);
      return;
    }

    setActive(navigation.isFocused());
    const unsubscribeFocus = navigation.addListener("focus", () =>
      setActive(true)
    );
    const unsubscribeBlur = navigation.addListener("blur", () =>
      setActive(false)
    );

    return () => {
      unsubscribeFocus();
      unsubscribeBlur();
    };
  }, [navigation]);

  useEffect(() => {
    reportLoaderActivity?.(boundaryId, {
      active,
      count: activeLoaderCount,
    });
  }, [active, activeLoaderCount, boundaryId, reportLoaderActivity]);

  useEffect(
    () => () => reportLoaderActivity?.(boundaryId, null),
    [boundaryId, reportLoaderActivity]
  );

  return (
    <LoaderActivityProvider onActiveLoaderCountChange={setActiveLoaderCount}>
      <LocaleContent key={appliedLanguage}>{children}</LocaleContent>
    </LoaderActivityProvider>
  );
};

const LocaleContent = ({ children }: { children: ReactNode }) => (
  <>{children}</>
);
